import React, { useEffect, useMemo } from 'react';
import { PhoneIcon } from '@heroicons/react/24/outline';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { nombreCliente } from '../../utils/turnoNuevo';

/**
 * A quién se le mandan los avisos del turno (WhatsApp de confirmación, listo
 * para retirar, etc.). "Automático" deja que el backend use su cadena
 * (contacto del vehículo → flota → cliente); las demás opciones mandan un
 * override explícito.
 */
export interface ContactoElegido {
  source: 'auto' | 'vehicle' | 'fleet' | 'client' | 'other' | 'custom';
  /** nombre y teléfono resueltos (vacíos en "auto") */
  name: string;
  phone: string;
  custom?: { name: string; lastName: string; role: string; phone: string };
}

export const CONTACTO_AUTO: ContactoElegido = { source: 'auto', name: '', phone: '' };

interface Opcion {
  key: string;
  source: ContactoElegido['source'];
  name: string;
  phone: string;
  detalle?: string;
}

interface Props {
  client: any | null;
  vehicle: any | null;
  vehicles: any[];
  value: ContactoElegido;
  onChange: (v: ContactoElegido) => void;
}

const soloDigitos = (p: any) => String(p || '').replace(/\D/g, '');

export const opcionesDeContacto = (client: any | null, vehicle: any | null, vehicles: any[]): Opcion[] => {
  const out: Opcion[] = [];
  if (vehicle?.contactPhone) {
    out.push({
      key: 'vehicle',
      source: 'vehicle',
      name: [vehicle.contactName, vehicle.contactLastName].filter(Boolean).join(' ') || 'Operario del vehículo',
      phone: vehicle.contactPhone,
      detalle: 'contacto del vehículo'
    });
  }
  if (client?.contactPhone) {
    out.push({ key: 'fleet', source: 'fleet', name: client.contactPerson || 'Contacto de flota', phone: client.contactPhone, detalle: 'flota' });
  }
  const telCliente = client?.whatsapp || client?.phone;
  if (telCliente) {
    out.push({ key: 'client', source: 'client', name: nombreCliente(client), phone: telCliente, detalle: 'cliente' });
  }
  const vistos = new Set(out.map((o) => soloDigitos(o.phone)));
  for (const v of vehicles || []) {
    if (!v || v.id === vehicle?.id) continue;
    const n = soloDigitos(v.contactPhone);
    if (!n || vistos.has(n)) continue;
    vistos.add(n);
    out.push({
      key: `other-${n}`,
      source: 'other',
      name: [v.contactName, v.contactLastName].filter(Boolean).join(' ') || 'Contacto',
      phone: v.contactPhone,
      detalle: v.licensePlate ? `usado en ${v.licensePlate}` : 'otro vehículo'
    });
  }
  return out;
};

const ContactoRecepcion: React.FC<Props> = ({ client, vehicle, vehicles, value, onChange }) => {
  const opciones = useMemo(() => opcionesDeContacto(client, vehicle, vehicles), [client, vehicle, vehicles]);
  const automatico = opciones[0] || null;

  // Si la opción elegida dejó de existir (cambió el vehículo), vuelve a automático
  useEffect(() => {
    if (value.source === 'auto' || value.source === 'custom') return;
    const sigue = opciones.some((o) => o.source === value.source && soloDigitos(o.phone) === soloDigitos(value.phone));
    if (!sigue) onChange(CONTACTO_AUTO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones]);

  const claveActual = value.source === 'auto'
    ? 'auto'
    : value.source === 'custom'
      ? 'custom'
      : (opciones.find((o) => o.source === value.source && soloDigitos(o.phone) === soloDigitos(value.phone))?.key || 'auto');

  const elegir = (key: string) => {
    if (key === 'auto') return onChange(CONTACTO_AUTO);
    if (key === 'custom') {
      const c = value.custom || { name: '', lastName: '', role: '', phone: '' };
      return onChange({ source: 'custom', name: [c.name, c.lastName].filter(Boolean).join(' '), phone: c.phone, custom: c });
    }
    const o = opciones.find((x) => x.key === key);
    if (o) onChange({ source: o.source, name: o.name, phone: o.phone });
  };

  const setCustom = (patch: Partial<NonNullable<ContactoElegido['custom']>>) => {
    const c = { ...(value.custom || { name: '', lastName: '', role: '', phone: '' }), ...patch };
    onChange({ source: 'custom', name: [c.name, c.lastName].filter(Boolean).join(' '), phone: c.phone, custom: c });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <PhoneIcon className="h-3.5 w-3.5" /> Avisos por WhatsApp a
        </span>
        <Select value={claveActual} onValueChange={elegir}>
          <SelectTrigger className="h-8 w-auto min-w-[220px] max-w-full text-sm" aria-label="Contacto para avisos">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">
              {automatico ? `Automático · ${automatico.name} ${automatico.phone}` : 'Automático · sin teléfono cargado'}
            </SelectItem>
            {opciones.map((o) => (
              <SelectItem key={o.key} value={o.key}>
                {o.name} · {o.phone}{o.detalle ? ` · ${o.detalle}` : ''}
              </SelectItem>
            ))}
            <SelectItem value="custom">Otro contacto…</SelectItem>
          </SelectContent>
        </Select>
        {value.source === 'auto' && !automatico && (
          <span className="text-xs text-muted-foreground">Sin teléfono no se manda ningún aviso.</span>
        )}
      </div>
      {value.source === 'custom' && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Input className="h-8" placeholder="Nombre" value={value.custom?.name || ''} onChange={(e) => setCustom({ name: e.target.value })} />
          <Input className="h-8" placeholder="Apellido" value={value.custom?.lastName || ''} onChange={(e) => setCustom({ lastName: e.target.value })} />
          <Input className="h-8" placeholder="Cargo (ej: chofer)" value={value.custom?.role || ''} onChange={(e) => setCustom({ role: e.target.value })} />
          <Input className="h-8" placeholder="Teléfono" inputMode="tel" value={value.custom?.phone || ''} onChange={(e) => setCustom({ phone: e.target.value })} />
        </div>
      )}
    </div>
  );
};

export default ContactoRecepcion;
